import { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from './constants';

export const useTimerSocket = (timerId: number) => {
	const [timerDisplay, setTimerDisplay] = useState<string>('--:--');
	const [timerState, setTimerState] = useState<'running' | 'paused'>('paused');
	const [isConnected, setIsConnected] = useState<boolean>(false);
	const socketRef = useRef<Socket | null>(null);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const hasPlayedWhistleRef = useRef<boolean>(false);

	useEffect(() => {
		// Initialize audio element
		audioRef.current = new Audio('/whistle.mp3');

		// Initialize socket connection
		const socket = io(config.wsUrl, {
			transports: ['websocket', 'polling'],
		});

		socketRef.current = socket;

		// Connection event handlers
		socket.on('connect', () => {
			console.log('WebSocket connected');
			setIsConnected(true);
			// Subscribe to this timer
			socket.emit('subscribe', timerId);
		});

		socket.on('disconnect', () => {
			console.log('WebSocket disconnected');
			setIsConnected(false);
		});

		socket.on('subscribed', (data: { timerId: number; room: string }) => {
			console.log('Subscribed to timer:', data.timerId);
		});

		// Listen for timer updates
		socket.on('timerUpdate', (update: any) => {
			console.log('Timer update received:', update);
			// Backend sends id as string, convert for comparison
			if (parseInt(update.id) === timerId || update.id === timerId) {
				console.log('Updating state:', update);
				setTimerDisplay(update.time);
				setTimerState(update.state);
			}
		});

		// Cleanup on unmount or timerId change
		return () => {
			if (socket) {
				socket.emit('unsubscribe', timerId);
				socket.disconnect();
			}
		};
	}, [timerId]);

	// Play whistle when timer reaches 0:00
	useEffect(() => {
		if (timerDisplay === '0:00' && !hasPlayedWhistleRef.current) {
			console.log('Timer reached 0:00, playing whistle');
			hasPlayedWhistleRef.current = true;
			audioRef.current?.play().catch((error) => {
				console.error('Failed to play whistle:', error);
			});
		}

		// Reset whistle flag when timer is reset or restarted
		if (timerDisplay !== '0:00' && hasPlayedWhistleRef.current) {
			hasPlayedWhistleRef.current = false;
		}
	}, [timerDisplay]);

	return {
		timerDisplay,
		timerState,
		isConnected,
	};
};
